import React, {
  useEffect,
  useRef,
  useState,
} from "react";

interface StatisticsSectionProps {
  statistics: {
    students: number;
    courses: number;
    teachers: number;
    awards: number;
  };
}

const StatisticsSection: React.FC<
  StatisticsSectionProps
> = ({ statistics }) => {
  const [counters, setCounters] = useState({
    students: 0,
    courses: 0,
    teachers: 0,
    awards: 0,
  });

  const animationRef =
    useRef<number | null>(null);

  // ============================================================
  // COUNTER ANIMATION
  // ============================================================

  useEffect(() => {
    const startTime = performance.now();
    const duration = 1800;

    const animateCounters = (
      currentTime: number
    ) => {
      const elapsed =
        currentTime - startTime;

      const progress = Math.min(
        elapsed / duration,
        1
      );

      // Smooth ease-out
      const easedProgress =
        1 -
        Math.pow(
          1 - progress,
          3
        );

      setCounters({
        students: Math.round(
          statistics.students *
            easedProgress
        ),

        courses: Math.round(
          statistics.courses *
            easedProgress
        ),

        teachers: Math.round(
          statistics.teachers *
            easedProgress
        ),

        awards: Math.round(
          statistics.awards *
            easedProgress
        ),
      });

      if (progress < 1) {
        animationRef.current =
          requestAnimationFrame(
            animateCounters
          );
      }
    };

    animationRef.current =
      requestAnimationFrame(
        animateCounters
      );

    return () => {
      if (
        animationRef.current !== null
      ) {
        cancelAnimationFrame(
          animationRef.current
        );
      }
    };
  }, [statistics]);

  // ============================================================
  // STATISTICS
  // ============================================================

  const statItems = [
    {
      key: "students",
      icon: "flaticon-user",
      value: counters.students,
      label: "Happy Students",
      description:
        "Students learning with us",
      color: "#00f6ff",
    },

    {
      key: "courses",
      icon: "flaticon-list-1",
      value: `${counters.courses}+`,
      label: "Our Courses",
      description:
        "Learning programs available",
      color: "#8b5cff",
    },

    {
      key: "teachers",
      icon: "flaticon-studying",
      value: counters.teachers,
      label: "Expert Teachers",
      description:
        "Experienced educators",
      color: "#00f6ff",
    },

    {
      key: "awards",
      icon: "flaticon-guarantee",
      value: counters.awards,
      label: "Awards Won",
      description:
        "Achievements & recognition",
      color: "#8b5cff",
    },
  ];

  return (
    <section className="growniverse-statistics">

      {/* ======================================================
          BACKGROUND
      ====================================================== */}

      <div className="statistics-background" />

      <div className="statistics-grid" />

      <div className="statistics-glow statistics-glow-left" />

      <div className="statistics-glow statistics-glow-right" />

      <div className="statistics-light-line" />

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <div className="statistics-container">

        {/* ====================================================
            HEADER
        ==================================================== */}

        <div className="statistics-heading">

          <div className="statistics-eyebrow">

            <span className="statistics-status-dot" />

            <span>
              OUR IMPACT
            </span>

            <span className="statistics-eyebrow-line" />

          </div>

          <h2 className="statistics-title">
            Growing
            <span> Together</span>
          </h2>

          <p className="statistics-subtitle">
            Every number represents a
            growing community of learners,
            educators and achievements.
          </p>

        </div>

        {/* ====================================================
            CARDS
        ==================================================== */}

        <div className="statistics-items">

          {statItems.map(
            (item, index) => (
              <div
                key={item.key}
                className="statistics-card"
                style={
                  {
                    "--stat-color":
                      item.color,
                    "--card-index":
                      index,
                  } as React.CSSProperties
                }
              >

                {/* Card glow */}

                <div className="statistics-card-glow" />

                {/* Top line */}

                <div className="statistics-card-top-line" />

                {/* Corner */}

                <div className="statistics-card-corner" />

                {/* ==================================================
                    ICON
                ================================================== */}

                <div className="statistics-icon-wrapper">

                  <div className="statistics-icon-ring">

                    <div className="statistics-icon">

                      <i
                        className={
                          item.icon
                        }
                      />

                    </div>

                  </div>

                  <span className="statistics-icon-status" />

                </div>

                {/* ==================================================
                    CONTENT
                ================================================== */}

                <div className="statistics-content">

                  <div className="statistics-number">

                    {item.value}

                  </div>

                  <div className="statistics-label">

                    {item.label}

                  </div>

                  <div className="statistics-description">

                    {item.description}

                  </div>

                  <div className="statistics-mini-line">

                    <span />

                  </div>

                </div>

                {/* ==================================================
                    CARD NUMBER
                ================================================== */}

                <div className="statistics-card-index">
                  0{index + 1}
                </div>

              </div>
            )
          )}

        </div>

        {/* ====================================================
            BOTTOM STATUS
        ==================================================== */}

        <div className="statistics-bottom-info">

          <div className="statistics-bottom-item">

            <span className="bottom-dot" />

            <span>
              Trusted Learning
            </span>

          </div>

          <div className="statistics-bottom-separator" />

          <div className="statistics-bottom-item">

            <span className="bottom-dot purple" />

            <span>
              Quality Education
            </span>

          </div>

          <div className="statistics-bottom-separator" />

          <div className="statistics-bottom-item">

            <span className="bottom-dot" />

            <span>
              Continuous Growth
            </span>

          </div>

        </div>

      </div>

      {/* ======================================================
          BOTTOM LINE
      ====================================================== */}

      <div className="statistics-bottom-line" />

      {/* ======================================================
          COMPLETE CSS
      ====================================================== */}

      <style>
        {`

/* ============================================================
   MAIN SECTION
   ============================================================ */

.growniverse-statistics {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  padding: 65px 20px 48px;

  background:
    radial-gradient(
      circle at 20% 0%,
      rgba(0,246,255,0.055),
      transparent 32%
    ),
    radial-gradient(
      circle at 85% 100%,
      rgba(139,92,255,0.065),
      transparent 35%
    ),
    linear-gradient(
      145deg,
      #03060d 0%,
      #060b16 48%,
      #040711 100%
    );

  border-top:
    1px solid
    rgba(0,246,255,0.10);

  border-bottom:
    1px solid
    rgba(139,92,255,0.12);

  box-shadow:
    inset 0 1px 0
    rgba(255,255,255,0.025),

    0 20px 60px
    rgba(0,0,0,0.35);

  isolation: isolate;
}


/* ============================================================
   BACKGROUND
   ============================================================ */

.statistics-background {
  position: absolute;

  inset: 0;

  z-index: -5;

  background:
    linear-gradient(
      180deg,
      rgba(255,255,255,0.012),
      transparent 30%,
      rgba(255,255,255,0.008)
    );

  pointer-events: none;
}


/* ============================================================
   GRID
   ============================================================ */

.statistics-grid {
  position: absolute;

  inset: 0;

  z-index: -4;

  pointer-events: none;

  opacity: 0.035;

  background-image:
    linear-gradient(
      rgba(255,255,255,0.09) 1px,
      transparent 1px
    ),

    linear-gradient(
      90deg,
      rgba(255,255,255,0.09) 1px,
      transparent 1px
    );

  background-size:
    42px 42px;

  mask-image:
    linear-gradient(
      to bottom,
      transparent,
      black 15%,
      black 85%,
      transparent
    );
}


/* ============================================================
   GLOWS
   ============================================================ */

.statistics-glow {
  position: absolute;

  width: 420px;
  height: 420px;

  border-radius: 50%;

  filter: blur(120px);

  pointer-events: none;

  z-index: -3;

  animation:
    statisticsGlowMove
    8s ease-in-out
    infinite alternate;
}


.statistics-glow-left {
  left: -300px;
  top: -250px;

  background:
    rgba(0,246,255,0.08);
}


.statistics-glow-right {
  right: -300px;
  bottom: -280px;

  background:
    rgba(139,92,255,0.09);

  animation-delay:
    -4s;
}


@keyframes statisticsGlowMove {

  from {
    transform:
      translate3d(0,0,0)
      scale(1);
  }

  to {
    transform:
      translate3d(35px,20px,0)
      scale(1.08);
  }

}


/* ============================================================
   LIGHT LINE
   ============================================================ */

.statistics-light-line {
  position: absolute;

  top: 0;

  left: 15%;
  right: 15%;

  height: 1px;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(0,246,255,0.45),
      rgba(139,92,255,0.40),
      transparent
    );

  box-shadow:
    0 0 14px
    rgba(0,246,255,0.18);

  pointer-events: none;
}


/* ============================================================
   CONTAINER
   ============================================================ */

.statistics-container {
  position: relative;

  z-index: 5;

  width: min(
    1200px,
    100%
  );

  margin:
    0 auto;
}


/* ============================================================
   HEADING
   ============================================================ */

.statistics-heading {
  text-align: center;

  margin-bottom: 42px;
}


/* ============================================================
   EYEBROW
   ============================================================ */

.statistics-eyebrow {
  display: flex;

  align-items: center;

  justify-content: center;

  gap: 9px;

  margin-bottom: 13px;

  color:
    rgba(190,250,255,0.78);

  font-size: 10px;

  font-weight: 800;

  letter-spacing: 3px;

  text-transform: uppercase;
}


.statistics-status-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background:
    #00f6ff;

  box-shadow:
    0 0 7px
    #00f6ff,

    0 0 15px
    rgba(0,246,255,0.45);

  animation:
    statisticsPulse
    2s ease-in-out
    infinite;
}


@keyframes statisticsPulse {

  0%,
  100% {
    opacity: 0.65;
    transform: scale(0.85);
  }

  50% {
    opacity: 1;
    transform: scale(1.15);
  }

}


.statistics-eyebrow-line {
  width: 35px;

  height: 1px;

  background:
    linear-gradient(
      90deg,
      #00f6ff,
      transparent
    );

  opacity: 0.65;
}


/* ============================================================
   TITLE
   ============================================================ */

.statistics-title {
  margin: 0;

  color: #ffffff;

  font-size:
    clamp(
      30px,
      4vw,
      48px
    );

  line-height: 1.05;

  font-weight: 800;

  letter-spacing: -1.5px;

  text-shadow:
    0 4px 25px
    rgba(0,0,0,0.45);
}


.statistics-title span {
  background:
    linear-gradient(
      90deg,
      #ffffff 0%,
      #bdfaff 40%,
      #a99aff 100%
    );

  -webkit-background-clip:
    text;

  background-clip:
    text;

  -webkit-text-fill-color:
    transparent;

  filter:
    drop-shadow(
      0 0 14px
      rgba(0,246,255,0.16)
    );
}


/* ============================================================
   SUBTITLE
   ============================================================ */

.statistics-subtitle {
  width:
    min(
      600px,
      100%
    );

  margin:
    15px auto 0;

  color:
    rgba(196,209,224,0.70);

  font-size: 14px;

  line-height: 1.65;

  font-weight: 400;
}


/* ============================================================
   ITEMS
   ============================================================ */

.statistics-items {
  display: grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0,1fr)
    );

  gap: 18px;
}


/* ============================================================
   CARD
   ============================================================ */

.statistics-card {
  --stat-color: #00f6ff;

  position: relative;

  min-width: 0;

  min-height: 180px;

  display: flex;

  align-items: center;

  gap: 18px;

  padding:
    25px 22px;

  box-sizing: border-box;

  overflow: hidden;

  border:
    1px solid
    color-mix(
      in srgb,
      var(--stat-color) 14%,
      transparent
    );

  border-radius: 18px;

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.065),
      rgba(255,255,255,0.018)
    );

  box-shadow:
    0 18px 45px
    rgba(0,0,0,0.28),

    inset 0 1px 0
    rgba(255,255,255,0.035);

  backdrop-filter:
    blur(15px);

  -webkit-backdrop-filter:
    blur(15px);

  transition:
    transform 0.35s ease,
    border-color 0.35s ease,
    box-shadow 0.35s ease,
    background 0.35s ease;

  animation:
    statisticsCardIn
    0.7s ease
    both;

  animation-delay:
    calc(
      var(--card-index) * 100ms
    );
}


@keyframes statisticsCardIn {

  from {
    opacity: 0;

    transform:
      translateY(18px);
  }

  to {
    opacity: 1;

    transform:
      translateY(0);
  }

}


/* ============================================================
   CARD HOVER
   ============================================================ */

.statistics-card:hover {
  transform:
    translateY(-7px);

  border-color:
    color-mix(
      in srgb,
      var(--stat-color) 42%,
      transparent
    );

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.085),
      rgba(255,255,255,0.025)
    );

  box-shadow:
    0 25px 55px
    rgba(0,0,0,0.42),

    0 0 30px
    color-mix(
      in srgb,
      var(--stat-color) 8%,
      transparent
    ),

    inset 0 1px 0
    rgba(255,255,255,0.05);
}


/* ============================================================
   CARD GLOW
   ============================================================ */

.statistics-card-glow {
  position: absolute;

  width: 150px;
  height: 150px;

  left: -70px;
  bottom: -90px;

  border-radius: 50%;

  background:
    var(--stat-color);

  opacity: 0.055;

  filter:
    blur(45px);

  pointer-events: none;

  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}


.statistics-card:hover
.statistics-card-glow {
  opacity: 0.13;

  transform:
    scale(1.4);
}


/* ============================================================
   TOP LINE
   ============================================================ */

.statistics-card-top-line {
  position: absolute;

  top: 0;

  left: 15%;
  right: 15%;

  height: 2px;

  background:
    linear-gradient(
      90deg,
      transparent,
      var(--stat-color),
      transparent
    );

  box-shadow:
    0 0 12px
    color-mix(
      in srgb,
      var(--stat-color) 55%,
      transparent
    );

  opacity: 0.8;
}


/* ============================================================
   CARD CORNER
   ============================================================ */

.statistics-card-corner {
  position: absolute;

  top: 12px;
  right: 12px;

  width: 18px;
  height: 18px;

  border-top:
    1px solid
    color-mix(
      in srgb,
      var(--stat-color) 45%,
      transparent
    );

  border-right:
    1px solid
    color-mix(
      in srgb,
      var(--stat-color) 45%,
      transparent
    );

  opacity: 0.6;
}


/* ============================================================
   ICON WRAPPER
   ============================================================ */

.statistics-icon-wrapper {
  position: relative;

  flex-shrink: 0;

  width: 62px;
  height: 62px;

  display: flex;

  align-items: center;

  justify-content: center;
}


/* ============================================================
   ICON RING
   ============================================================ */

.statistics-icon-ring {
  width: 58px;
  height: 58px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 50%;

  border:
    1px solid
    color-mix(
      in srgb,
      var(--stat-color) 24%,
      transparent
    );

  background:
    radial-gradient(
      circle,
      color-mix(
        in srgb,
        var(--stat-color) 8%,
        transparent
      ),
      rgba(255,255,255,0.01)
    );

  box-shadow:
    0 0 22px
    color-mix(
      in srgb,
      var(--stat-color) 8%,
      transparent
    );

  transition:
    transform 0.35s ease,
    box-shadow 0.35s ease;
}


.statistics-card:hover
.statistics-icon-ring {
  transform:
    rotate(8deg)
    scale(1.06);

  box-shadow:
    0 0 30px
    color-mix(
      in srgb,
      var(--stat-color) 16%,
      transparent
    );
}


/* ============================================================
   ICON
   ============================================================ */

.statistics-icon {
  width: 43px;
  height: 43px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 50%;

  background:
    color-mix(
      in srgb,
      var(--stat-color) 7%,
      transparent
    );
}


.statistics-icon i {
  color:
    var(--stat-color);

  font-size: 21px;

  filter:
    drop-shadow(
      0 0 8px
      color-mix(
        in srgb,
        var(--stat-color) 65%,
        transparent
      )
    );
}


/* ============================================================
   ICON STATUS
   ============================================================ */

.statistics-icon-status {
  position: absolute;

  right: 1px;
  bottom: 5px;

  width: 7px;
  height: 7px;

  border-radius: 50%;

  background:
    var(--stat-color);

  border:
    2px solid
    #07101b;

  box-shadow:
    0 0 9px
    var(--stat-color);
}


/* ============================================================
   CONTENT
   ============================================================ */

.statistics-content {
  position: relative;

  min-width: 0;

  flex: 1;
}


/* ============================================================
   NUMBER
   ============================================================ */

.statistics-number {
  color: #ffffff;

  font-size:
    clamp(
      28px,
      3vw,
      39px
    );

  line-height: 0.95;

  font-weight: 850;

  letter-spacing: -1.5px;

  white-space: nowrap;

  text-shadow:
    0 0 20px
    color-mix(
      in srgb,
      var(--stat-color) 18%,
      transparent
    );
}


/* ============================================================
   LABEL
   ============================================================ */

.statistics-label {
  margin-top: 8px;

  color:
    rgba(238,246,255,0.94);

  font-size: 12px;

  line-height: 1.2;

  font-weight: 700;

  letter-spacing: 0.2px;

  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;
}


/* ============================================================
   DESCRIPTION
   ============================================================ */

.statistics-description {
  margin-top: 5px;

  color:
    rgba(157,174,193,0.65);

  font-size: 9px;

  line-height: 1.35;

  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;
}


/* ============================================================
   MINI LINE
   ============================================================ */

.statistics-mini-line {
  width: 38px;

  height: 2px;

  margin-top: 12px;

  overflow: hidden;

  border-radius: 10px;

  background:
    rgba(255,255,255,0.10);
}


.statistics-mini-line span {
  display: block;

  width: 65%;

  height: 100%;

  border-radius: inherit;

  background:
    var(--stat-color);

  box-shadow:
    0 0 9px
    var(--stat-color);
}


/* ============================================================
   CARD INDEX
   ============================================================ */

.statistics-card-index {
  position: absolute;

  right: 16px;
  bottom: 13px;

  color:
    color-mix(
      in srgb,
      var(--stat-color) 30%,
      transparent
    );

  font-size: 9px;

  font-weight: 800;

  letter-spacing: 1px;
}


/* ============================================================
   BOTTOM INFO
   ============================================================ */

.statistics-bottom-info {
  display: flex;

  align-items: center;

  justify-content: center;

  flex-wrap: wrap;

  gap: 18px;

  margin-top: 30px;

  color:
    rgba(170,187,205,0.62);

  font-size: 9px;

  font-weight: 600;

  letter-spacing: 0.7px;

  text-transform: uppercase;
}


.statistics-bottom-item {
  display: flex;

  align-items: center;

  gap: 7px;
}


.bottom-dot {
  width: 5px;
  height: 5px;

  border-radius: 50%;

  background:
    #00f6ff;

  box-shadow:
    0 0 8px
    rgba(0,246,255,0.75);
}


.bottom-dot.purple {
  background:
    #8b5cff;

  box-shadow:
    0 0 8px
    rgba(139,92,255,0.75);
}


.statistics-bottom-separator {
  width: 1px;

  height: 12px;

  background:
    rgba(255,255,255,0.12);
}


/* ============================================================
   BOTTOM LINE
   ============================================================ */

.statistics-bottom-line {
  position: absolute;

  bottom: 0;

  left: 12%;
  right: 12%;

  height: 1px;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(0,246,255,0.40),
      rgba(139,92,255,0.40),
      transparent
    );

  box-shadow:
    0 0 12px
    rgba(0,246,255,0.18);

  pointer-events: none;
}


/* ============================================================
   TABLET
   ============================================================ */

@media (max-width: 991px) {

  .growniverse-statistics {
    padding:
      55px 18px 42px;
  }

  .statistics-items {
    grid-template-columns:
      repeat(2, minmax(0,1fr));

    gap: 15px;
  }

  .statistics-card {
    min-height: 160px;

    padding:
      22px 20px;
  }

}


/* ============================================================
   MOBILE
   ============================================================ */

@media (max-width: 576px) {

  .growniverse-statistics {
    padding:
      38px 12px 30px;
  }


  .statistics-heading {
    margin-bottom: 25px;
  }


  .statistics-eyebrow {
    font-size: 8px;

    letter-spacing: 2.3px;
  }


  .statistics-title {
    font-size: 30px;

    letter-spacing: -1px;
  }


  .statistics-subtitle {
    margin-top: 10px;

    font-size: 11px;

    line-height: 1.55;

    padding:
      0 12px;
  }


  .statistics-items {
    display: flex;

    flex-direction: column;

    gap: 8px;
  }


  /* ==========================================================
     MOBILE CARD
     ========================================================== */

  .statistics-card {
    width: 100%;

    min-height: 68px;

    height: 68px;

    padding:
      10px 13px;

    gap: 12px;

    border-radius: 11px;

    transform: none !important;

    animation:
      statisticsCardMobileIn
      0.5s ease
      both;
  }


  @keyframes statisticsCardMobileIn {

    from {
      opacity: 0;

      transform:
        translateX(-10px) !important;
    }

    to {
      opacity: 1;

      transform:
        translateX(0) !important;
    }

  }


  .statistics-card:hover {
    transform: none !important;
  }


  /* ==========================================================
     MOBILE ICON
     ========================================================== */

  .statistics-icon-wrapper {
    width: 43px;
    height: 43px;
  }


  .statistics-icon-ring {
    width: 40px;
    height: 40px;
  }


  .statistics-icon {
    width: 31px;
    height: 31px;
  }


  .statistics-icon i {
    font-size: 15px;
  }


  .statistics-icon-status {
    width: 5px;
    height: 5px;

    right: 0;

    bottom: 4px;

    border-width: 1px;
  }


  /* ==========================================================
     MOBILE NUMBER
     ========================================================== */

  .statistics-number {
    font-size: 22px;

    letter-spacing: -0.5px;
  }


  .statistics-label {
    margin-top: 3px;

    font-size: 10px;
  }


  .statistics-description {
    display: none;
  }


  .statistics-mini-line {
    display: none;
  }


  .statistics-card-index {
    right: 10px;
    bottom: 8px;

    font-size: 7px;
  }


  /* ==========================================================
     MOBILE BOTTOM
     ========================================================== */

  .statistics-bottom-info {
    gap: 10px;

    margin-top: 20px;

    font-size: 7px;

    letter-spacing: 0.4px;
  }


  .statistics-bottom-separator {
    height: 9px;
  }


  .statistics-bottom-line {
    left: 8%;
    right: 8%;
  }


  .statistics-glow {
    opacity: 0.55;
  }

}


/* ============================================================
   VERY SMALL PHONES
   ============================================================ */

@media (max-width: 380px) {

  .growniverse-statistics {
    padding:
      30px 9px 25px;
  }


  .statistics-title {
    font-size: 27px;
  }


  .statistics-subtitle {
    font-size: 10px;
  }


  .statistics-card {
    height: 62px;

    min-height: 62px;

    padding:
      8px 10px;

    gap: 9px;
  }


  .statistics-icon-wrapper {
    width: 38px;
    height: 38px;
  }


  .statistics-icon-ring {
    width: 36px;
    height: 36px;
  }


  .statistics-icon {
    width: 28px;
    height: 28px;
  }


  .statistics-icon i {
    font-size: 13px;
  }


  .statistics-number {
    font-size: 20px;
  }


  .statistics-label {
    font-size: 9px;
  }


  .statistics-bottom-info {
    gap: 7px;

    font-size: 6px;
  }

}


/* ============================================================
   ACCESSIBILITY
   ============================================================ */

@media (prefers-reduced-motion: reduce) {

  .statistics-glow,
  .statistics-status-dot,
  .statistics-card {
    animation: none !important;
  }

  .statistics-card,
  .statistics-icon-ring,
  .statistics-card-glow {
    transition: none !important;
  }

}

        `}
      </style>

    </section>
  );
};

export default StatisticsSection;